import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay } from '../money.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { StockLocationOption } from './Purchasing.js'
import type { StockPositionData } from './Stock.js'

/**
 * Moving stock between branches. §6.10 `INV-004`, §12 case 46. Posts to `POST /stock/transfers`
 * and `POST /stock/transfers/:transferId/receipt`.
 *
 * **Nothing in the product could do either, and until migration 0046 nothing could have.** A
 * transfer recorded no destination, so there was no list of what was coming to a branch and no way
 * to tell a receipt at the right branch from one at the wrong one: `receiveTransfer` compared the
 * receiving location's branch against the caller, which every receiver passes. The screen that
 * needed the column is this one.
 *
 * **In transit is the subject, not the transfer.** `INV-004` makes the in-transit state the thing
 * `INV-002` has to value and §12 case 46 makes it owned stock on the road, so the list is what has
 * left and not arrived, in both directions, because a shopkeeper's question is "what is moving that
 * concerns me" and the two ends are one answer. A received transfer leaves the list: it is on a
 * shelf and the shelf screen is where it belongs.
 *
 * **What the dispatch half refuses before sending**, because each is a 422 or 409 and a refused
 * write holds the head of a serial queue (`OFF-001a`):
 *
 * - A destination that is not a branch of this shop, and the branch the stock is already at, which
 *   `dispatchTransfer` refuses with `TRANSFER_DESTINATION_UNKNOWN` and
 *   `TRANSFER_DESTINATION_IS_SOURCE`. Both are impossible here: the chooser offers the shop's own
 *   branches minus the one the position is at.
 * - More than the shelf holds, which is `INSUFFICIENT_STOCK`. The quantity is checked against the
 *   position's own figure, which is the figure on screen beside it.
 * - A stale version, which is `EXPECTED_VERSION_STALE`. The lock travels from the position row, so
 *   a shelf somebody else moved while the form was open is a conflict rather than an overwrite.
 *
 * **What the receive half refuses**, and the one it cannot:
 *
 * - A receipt at the wrong branch is `TRANSFER_WRONG_BRANCH`, and the form offers only this
 *   branch's own locations, so it cannot be reached from here.
 * - More than was sent is `TRANSFER_OVER_RECEIPT`; the dispatched figure is on screen and checked.
 * - **A short receipt is not a refusal and must not look like one.** §15.6 closes it as `short`
 *   with the missing value held rather than written off, so the form says what will happen when the
 *   figure is lower rather than warning somebody off entering the truth.
 */

/** A transfer in transit, as the read gives it. */
export interface TransferData {
  readonly transferId: string
  /** `ARC-020`: unnarrowed, so a status this build has not met renders as itself. */
  readonly status: string
  readonly fromBranchId: string
  /** Absent for a transfer dispatched before the destination was recorded. */
  readonly toBranchId?: string
  readonly dispatchedQuantity: number
  readonly dispatchedAt: string
  /** Cost carried with the goods. Absent without Appendix B's cost row (`ROLE-001a`). */
  readonly valueMinor?: string
  readonly currency: string
}

export interface TransfersProps {
  /** This branch, which decides what is incoming and where a receipt may land. */
  readonly branchId: string
  /** The shop's branches, for the destination chooser. */
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  readonly loadTransfers: () => Promise<readonly TransferData[]>
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  readonly loadPositions: (locationId: string) => Promise<readonly StockPositionData[]>
  /** `stock_transfer_out_in`, the row both writes take. */
  readonly canMove: boolean
  readonly timezone: string
  readonly onWritten?: () => void
}

export function Transfers({
  branchId,
  branches,
  loadTransfers,
  loadLocations,
  loadPositions,
  canMove,
  onWritten,
}: TransfersProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [transfers, setTransfers] = useState<readonly TransferData[] | null>(null)
  const [locations, setLocations] = useState<readonly StockLocationOption[]>([])
  const [fromLocation, setFromLocation] = useState('')
  const [positions, setPositions] = useState<readonly StockPositionData[]>([])
  const [itemId, setItemId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [toBranch, setToBranch] = useState('')
  const [receiving, setReceiving] = useState<string | null>(null)
  const [toLocation, setToLocation] = useState('')
  const [received, setReceived] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Apart from `error`, which carries the send and receive validation (whole pieces, more than the
  // shelf holds): a mistyped quantity must not turn the in-transit list into a failed load.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    void loadTransfers()
      .then((loaded) => {
        if (cancelled) return
        setTransfers(loaded)
        setLoadError(null)
      })
      .catch(() => {
        if (cancelled) return
        setTransfers(null)
        setLoadError(t('transfers.loadFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [loadTransfers, t, attempt])

  useEffect(() => {
    let cancelled = false
    void loadLocations()
      .then((loaded) => {
        if (cancelled) return
        setLocations(loaded)
        // The only shelf there is, is not a choice. More than one is.
        if (loaded.length === 1) setFromLocation(loaded[0]?.id ?? '')
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [loadLocations])

  useEffect(() => {
    if (fromLocation === '') {
      setPositions([])
      return
    }
    let cancelled = false
    void loadPositions(fromLocation)
      .then((loaded) => {
        if (!cancelled) setPositions(loaded)
      })
      .catch(() => {
        if (!cancelled) setError(t('transfers.shelfFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [fromLocation, loadPositions, t])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setQuantity('')
    setReceived('')
    setReceiving(null)
    onWritten?.()
    // **Through the effect, not a second call path.** This used to read the list itself and end in
    // `.catch(() => undefined)`, so a reload that failed right after a transfer was recorded left
    // the previous list on screen with nothing said: the shopkeeper looks for the transfer they
    // just sent, does not find it, and the screen is telling them it is up to date. Bumping
    // `attempt` re-runs the loading effect, which already sets `transfers` to null and
    // `transfers.loadFailed` on a failure and offers Try again.
    setAttempt((a) => a + 1)
  })

  const chosen = positions.find((p) => p.stockItemId === itemId)
  /** The shop's other branches: the destination is somewhere else by definition. */
  const destinations = branches.filter((b) => b.id !== branchId)
  const branchName = (id: string | undefined): string =>
    id === undefined
      ? t('transfers.unknownBranch')
      : (branches.find((b) => b.id === id)?.name ?? id)

  const pieces = useCallback(
    (text: string, ceiling: number, over: string): number | null => {
      const value = Number(text.trim())
      if (text.trim() === '' || !Number.isSafeInteger(value) || value < 1) {
        setError(t('transfers.quantityWhole'))
        return null
      }
      if (value > ceiling) {
        setError(over)
        return null
      }
      return value
    },
    [t],
  )

  const dispatch = useCallback(async () => {
    if (chosen === undefined) {
      setError(t('transfers.pickAnItem'))
      return
    }
    if (toBranch === '') {
      setError(t('transfers.pickABranch'))
      return
    }
    const count = pieces(quantity, chosen.quantity, t('transfers.moreThanShelf'))
    if (count === null) return

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/stock/transfers',
      label: t('transfers.queueSendLabel'),
      body: {
        stockItemId: chosen.stockItemId,
        quantity: count,
        toBranchId: toBranch,
        // Appendix J's lock, from the row on screen: a shelf somebody else moved while this form
        // was open is a conflict and not an overwrite.
        expectedVersion: chosen.version,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [chosen, enqueue, pieces, quantity, t, toBranch])

  const receive = useCallback(
    async (transfer: TransferData) => {
      if (toLocation === '') {
        setError(t('transfers.pickAShelf'))
        return
      }
      const count = pieces(
        received,
        transfer.dispatchedQuantity,
        t('transfers.moreThanSent', { sent: transfer.dispatchedQuantity }),
      )
      if (count === null) return

      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/stock/transfers/${transfer.transferId}/receipt`,
        label: t('transfers.queueReceiveLabel'),
        body: {
          toLocationId: toLocation,
          receivedQuantity: count,
          // No `expectedVersion`: the receipt creates the arriving position rather than editing
          // one, and `receiveTransfer` refuses a second receipt on the transfer's own state.
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, pieces, received, t, toLocation],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.transfer'))

  return (
    <section className="lm-screen" aria-labelledby="transfers-heading">
      <h1 id="transfers-heading" className="lm-screen__heading">
        {t('transfers.heading')}
      </h1>
      <p className="lm-hint">{t('transfers.inTransitMeans')}</p>

      {/*
        §8 `ST-02`. Empty is the steady state here and not a first run, so there is one empty
        sentence and no filtered one. It says where stock appears rather than inviting a send,
        because a cashier who cannot move stock reads this too. Partial is the cost grant
        (`ROLE-001a`) withholding the value, which is policy and shows on every visit without it.
      */}
      <DataSurface
        state={
          loadError !== null
            ? { kind: 'error', message: loadError, onRetry: () => setAttempt((a) => a + 1) }
            : transfers === null
              ? { kind: 'loading' }
              : transfers.length === 0
                ? { kind: 'empty' }
                : transfers.some((x) => x.valueMinor === undefined)
                  ? { kind: 'partial', missing: t('transfers.missingValue') }
                  : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('transfers.what')}
        emptyMessage={t('transfers.empty')}
      >
        <ul className="lm-transfer-list">
          {(transfers ?? []).map((transfer) => {
            const incoming = transfer.toBranchId === branchId
            return (
              <li key={transfer.transferId} className="lm-transfer">
                <span className="lm-transfer__way">
                  {incoming ? t('transfers.coming') : t('transfers.going')}
                </span>
                <span className="lm-transfer__where">
                  {incoming
                    ? t('transfers.fromBranch', { branch: branchName(transfer.fromBranchId) })
                    : t('transfers.toBranch', { branch: branchName(transfer.toBranchId) })}
                </span>
                <span className="lm-transfer__count" dir="ltr">
                  <Ltr>{String(transfer.dispatchedQuantity)}</Ltr>
                </span>
                {transfer.valueMinor === undefined ? null : (
                  <span className="lm-transfer__value" dir="ltr">
                    <Ltr>{formatWireForDisplay(transfer.valueMinor, transfer.currency)}</Ltr>
                  </span>
                )}

                {!canMove ? null : !incoming ? (
                  // Said rather than offered: the branch that sent it cannot book it in, and a
                  // button here would queue a `TRANSFER_WRONG_BRANCH` that holds the till.
                  <span className="lm-hint">{t('transfers.theirsToReceive')}</span>
                ) : receiving === transfer.transferId ? (
                  <>
                    <label className="lm-field lm-field--inline">
                      <span className="lm-field__label">{t('transfers.shelfLabel')}</span>
                      <select
                        className="lm-input"
                        value={toLocation}
                        onChange={(e) => {
                          setToLocation(e.currentTarget.value)
                        }}
                      >
                        <option value="">{t('transfers.pickAShelfOption')}</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="lm-field lm-field--inline">
                      <span className="lm-field__label">{t('transfers.receivedLabel')}</span>
                      <input
                        className="lm-input"
                        dir="ltr"
                        inputMode="numeric"
                        autoComplete="off"
                        value={received}
                        onChange={(e) => {
                          setReceived(e.currentTarget.value)
                        }}
                      />
                    </label>
                    {/* §15.6: fewer arriving is a discrepancy to decide, not a mistake to prevent.
                        Said before the figure is typed, so the truth is easier to enter than a
                        round number. */}
                    <p className="lm-hint">{t('transfers.shortMeans')}</p>
                    <button
                      type="button"
                      className="lm-button lm-button--primary"
                      disabled={writeId !== null}
                      onClick={() => {
                        void receive(transfer)
                      }}
                    >
                      {t('transfers.bookIn')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setReceiving(transfer.transferId)
                      setReceived('')
                      setError(null)
                    }}
                  >
                    {t('transfers.startReceiving')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      {canMove ? (
        <fieldset className="lm-transfer-send">
          <legend>{t('transfers.sendLegend')}</legend>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('transfers.fromShelfLabel')}</span>
            <select
              className="lm-input"
              value={fromLocation}
              onChange={(e) => {
                setFromLocation(e.currentTarget.value)
                setItemId('')
              }}
            >
              <option value="">{t('transfers.pickAShelfOption')}</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('transfers.itemLabel')}</span>
            <select
              className="lm-input"
              value={itemId}
              onChange={(e) => {
                setItemId(e.currentTarget.value)
              }}
            >
              <option value="">{t('transfers.pickAnItemOption')}</option>
              {positions.map((position) => (
                <option key={position.stockItemId} value={position.stockItemId}>
                  {/* What it is, its lot and what is on it. Two positions can be the same frame,
                      so the lot is what tells them apart and the figure is what decides how many
                      can go. The item itself was missing entirely, and a shop that keeps no lot
                      numbers read `Lot , 10 on hand`. */}
                  {t(position.lot === '' ? 'transfers.itemOptionNoLot' : 'transfers.itemOption', {
                    item: position.label ?? t('stock.unknownItem'),
                    lot: position.lot,
                    quantity: position.quantity,
                  })}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('transfers.toBranchLabel')}</span>
            <select
              className="lm-input"
              value={toBranch}
              onChange={(e) => {
                setToBranch(e.currentTarget.value)
              }}
            >
              <option value="">{t('transfers.pickABranchOption')}</option>
              {destinations.map((destination) => (
                <option key={destination.id} value={destination.id}>
                  {destination.name}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('transfers.quantityLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.currentTarget.value)
              }}
            />
          </label>
          <p className="lm-hint">{t('transfers.sendMeans')}</p>

          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={writeId !== null}
            onClick={() => {
              void dispatch()
            }}
          >
            {t('transfers.send')}
          </button>
        </fieldset>
      ) : (
        <p className="lm-hint">{t('transfers.needsTheRow')}</p>
      )}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}
    </section>
  )
}
